import React, { Suspense, useState } from 'react'
import classNames from 'classnames'
import styled from 'styled-components'
import { Button, Row, Col, Container, ListGroup } from 'react-bootstrap'
import { NavLink, Link, Switch, Route, useRouteMatch, useParams } from 'react-router-dom'
import PlotOverview from 'components/PlotOverview'
import Plots from 'components/Plots'
import Plot from 'components/Plot'
import Deiliskipulag from 'components/Plot/Deiliskipulag'
import SanitizedHtml from 'components/SanitizedHtml'
import Spinner from 'components/Spinner'
import useHverfi from 'hooks/useHverfi'

const Styles = styled.div`
  padding: 1.5rem 0 0 0;

  ul {
    list-style: none;
    padding: 0;
    margin: 0;
  }

  li {
    padding: 10px 0;
  }

  li:not(:last-child) {
    border-bottom: 1px solid #d4d4d4;
  }

  h1,
  h2,
  h3,
  h4,
  h5,
  h6 {
    font-weight: normal;
    color: var(--primary);
    font-size: 16px;
    margin: 0 0 6px 0;
    padding: 0;
  }

  a {
    color: #4a4a4a;
  }

  a[href$='pdf'],
  a[href$='xls'],
  a[href$='xlsx'] {
    display: flex;
    align-items: center;
    gap: 8px;
    line-height: 1;

    &:before {
      display: block;
      width: 15px;
      height: 15px;
    }
  }

  a[href$='xls'],
  a[href$='xlsx'] {
    &:before {
      content: url('data:image/svg+xml;utf8,%3Csvg%20stroke%3D%22rgb%2874%2C74%2C74%29%22%20fill%3D%22rgb%2874%2C74%2C74%29%22%20stroke-width%3D%220%22%20role%3D%22img%22%20viewBox%3D%220%200%2024%2024%22%20height%3D%2215px%22%20width%3D%2215px%22%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%3E%3Cpath%20d%3D%22M23%201.5q.41%200%20.7.3.3.29.3.7v19q0%20.41-.3.7-.29.3-.7.3H7q-.41%200-.7-.3-.3-.29-.3-.7V18H1q-.41%200-.7-.3-.3-.29-.3-.7V7q0-.41.3-.7Q.58%206%201%206h5V2.5q0-.41.3-.7.29-.3.7-.3zM6%2013.28l1.42%202.66h2.14l-2.38-3.87%202.34-3.8H7.46l-1.3%202.4-.05.08-.04.09-.64-1.28-.66-1.29H2.59l2.27%203.82-2.48%203.85h2.16zM14.25%2021v-3H7.5v3zm0-4.5v-3.75H12v3.75zm0-5.25V7.5H12v3.75zm0-5.25V3H7.5v3zm8.25%2015v-3h-6.75v3zm0-4.5v-3.75h-6.75v3.75zm0-5.25V7.5h-6.75v3.75zm0-5.25V3h-6.75v3Z%22%3E%3C%2Fpath%3E%3C%2Fsvg%3E');
    }
  }

  a[href$='pdf']:before {
    content: url('data:image/svg+xml;utf8,%3Csvg%20xmlns%3D%27http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%27%20stroke%3D%27rgb%2874%2C74%2C74%29%27%20fill%3D%27rgb%2874%2C74%2C74%29%27%20stroke-width%3D%270%27%20viewBox%3D%270%200%2024%2024%27%20width%3D%2715px%27%20height%3D%2715px%27%20%3E%3Cpath%20fill%3D%27none%27%20stroke%3D%27%23000%27%20stroke-width%3D%272%27%20d%3D%27M4.99787498%2C8.99999999%20L4.99787498%2C0.999999992%20L19.4999998%2C0.999999992%20L22.9999998%2C4.50000005%20L23%2C23%20L4%2C23%20M18%2C1%20L18%2C6%20L23%2C6%20M3%2C12%20L3.24999995%2C12%20L4.49999995%2C12%20C6.5%2C12%206.75%2C13.25%206.75%2C14%20C6.75%2C14.75%206.5%2C16%204.49999995%2C16%20L3.24999995%2C16%20L3.24999995%2C18%20L3%2C17.9999999%20L3%2C12%20Z%20M9.5%2C18%20L9.5%2C12%20C9.5%2C12%2010.4473684%2C12%2011.2052633%2C12%20C12.3421053%2C12%2013.5%2C12.5%2013.5%2C15%20C13.5%2C17.5%2012.3421053%2C18%2011.2052633%2C18%20C10.4473684%2C18%209.5%2C18%209.5%2C18%20Z%20M16.5%2C19%20L16.5%2C12%20L20.5%2C12%20M16.5%2C15.5%20L19.5%2C15.5%27%3E%3C%2Fpath%3E%3C%2Fsvg%3E');
  }
`

const Lodir = () => {
  const params = useParams()
  const { image, afangar, data } = useHverfi(params.hverfi)
  const { path, url } = useRouteMatch()
  const [hover, setHover] = useState(null)

  return data ? (
    <>
      <Row className="align-items-center justify-content-center h-100 m-0">
        <Col className="py-4">
          <h2 className="mb-1 text-primary">{data.title}</h2>
          <SanitizedHtml html={data.body_full} />
          {afangar.length > 1 ? (
            <Row className="m-n1">
              {afangar.map(({ btnSpan, forSale, viewable, key, title, subtitle, status }) => {
                let variant = forSale ? 'outline-primary' : 'outline-dark'

                if (status === 'allocated') {
                  variant = 'outline-danger'
                }

                return (
                  <Col key={key} className="p-1" md={btnSpan ?? 6}>
                    <Button
                      as={forSale || viewable ? NavLink : Link}
                      className={classNames('w-100 px-0', {
                        active: key === hover,
                      })}
                      variant={variant}
                      to={forSale || viewable ? `${url}/${key}` : url}
                      onMouseEnter={() => setHover(key)}
                      onMouseLeave={() => setHover(null)}
                      style={{
                        lineHeight: 1.4,
                      }}
                    >
                      {title}
                    </Button>
                  </Col>
                )
              })}
            </Row>
          ) : null}

          {data.body_gogn || data.gogn ? (
            <Styles>
              <SanitizedHtml html={data.body_gogn} />
              {Array.isArray(data?.gogn) ? (
                <ul>
                  {data.gogn.map(({ item }) => (
                    <li key={item.id}>
                      <a href={item.slod} target="_blank" rel="noopener noreferrer">
                        {item.title}
                      </a>
                    </li>
                  ))}
                </ul>
              ) : null}
            </Styles>
          ) : null}
        </Col>
        <Col lg={7} xl={8} className="px-0 align-self-start">
          <Switch>
            <Route exact path={path}>
              <PlotOverview afangar={afangar} hover={hover} handleHover={setHover} image={image} />
            </Route>
            <Route path={`${path}/:afangi`}>
              <Plots afangar={afangar} />
            </Route>
          </Switch>
        </Col>
      </Row>

      <Route path={`${path}/:afangi/:lod`}>
        <Plot lodir={data.lodir} />
        <Deiliskipulag data={data.afangar} />
      </Route>
    </>
  ) : (
    <Spinner />
  )
}

export default Lodir
